import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../components/Icon/';
import { useTranslation } from 'react-i18next';
import '../../../i18n';
import { useGlobalState } from '../../contexts/GlobalStateContext';

export default function NavigationList({ baseName = "", unauthenticated = false, newConstruction }) {
    window.addEventListener('load', () => {
        // After DOM loaded
        currentPage();
    });

    const { t } = useTranslation();
    const { globalState } = useGlobalState();

    const isAdmin = globalState.activeUserCompany?.roles?.toLowerCase().includes('portal admin') ?? false;

    useEffect(() => {
        // Clear all current highlights before re-applying
        const allCurrentItems = document.querySelectorAll('.menu-item-current');
        allCurrentItems.forEach(item => item.classList.remove('menu-item-current'));

        // After DOM update and when company/role changes, re-highlight current page
        currentPage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [globalState.activeUserCompany, isAdmin]); // Re-run when company or admin status changes

    const nav = [
        {
            title: t('nav.home'),
            to: '/home',
            icon: 'Home',
        },
        {
            title: t('nav.add-order'),
            to: '/order-add',
            icon: 'AddOrder',
        },
        {
            title: t('nav.orders'),
            icon: 'Orders',
            children: [
                {
                    title: t('nav.orders-open'),
                    to: '/orders',
                },
                {
                    title: t('nav.orders-closed'),
                    to: '/order-history',
                },
            ],
        },
        {
            title: t('nav.my-pages'),
            icon: 'MyPages',
            children: [
                /*{
                    title: t('nav.campaigns'),
                    to: '/campaigns',
                },*/
                {
                    title: t('nav.products-in-showroom'),
                    to: '/my-pages/products-in-showroom',
                },
                {
                    title: t('nav.add-products-in-showroom'),
                    to: '/my-pages/add-products',
                },
            ],
        },
        {
            title: t('nav.finance'),
            icon: 'Economy',
            children: [
                {
                    title: t('nav.finance-dashboard'),
                    to: '/finance',
                },
                {
                    title: t('nav.invoices'),
                    to: '/invoices',
                },
            ],
        },
        {
            title: t('nav.complaints'),
            icon: 'Complaints',
            children: [
                {
                    title: t('nav.complaint-new'),
                    to: '/complaint-new',
                },
                {
                    title: t('nav.complaints-open'),
                    to: '/complaints',
                },
                {
                    title: t('nav.complaints-closed'),
                    to: '/complaints-history',
                },
            ],
        },
        {
            title: t('nav.download'),
            icon: 'Download',
            unauthenticated: true,
            children: [
                {
                    title: t('nav.download-documents'),
                    to: '/media',
                    unauthenticated: false
                },
                {
                    title: t('nav.download-signs'),
                    to: '/media-signs',
                    unauthenticated: true
                },
                {
                    title: t('nav.download-dop'),
                    to: '/media-dop',
                    unauthenticated: true
                },
                {
                    title: t('nav.download-manuals'),
                    to: '/media-manuals',
                    unauthenticated: true
                },
                {
                    title: t('nav.download-images'),
                    to: '/media-high-resolution',
                    unauthenticated: true
                },
                {
                    title: t('nav.download-video'),
                    to: '/media-video',
                    unauthenticated: true
                },
                {
                    title: t('nav.download-campaign'),
                    to: '/media-campaign',
                    unauthenticated: true
                },
            ],
        },
        {
            title: t('nav.news'),
            to: '/news',
            icon: 'Bell',
        },
        {
            title: t('nav.contact'),
            to: '/contact',
            icon: 'Mail',
        },

    ];


    let newConstructionNav = [
        {
            title: t('nav.download-documents'),
            to: '/media',
        },
        {
            title: t('nav.download-images'),
            to: '/media-high-resolution',
        },

        {
            title: t('nav.download-signs'),
            to: '/media-signs',
        },
        {
            title: t('nav.download-dop'),
            to: '/media-dop',
        },
        {
            title: t('nav.download-manuals'),
            to: '/media-manuals',
        },

        {
            title: t('nav.download-video'),
            to: '/media-video',
        },
        {
            title: "Contura.eu",
            external: true,
            to: 'https://www.contura.eu/sv-se',
        },
        {
            title: "Premodul.com",
            external: true,
            to: 'https://www.premodul.com/se',
        },
        {
            title: "Skorstensberäknare",
            external: true,
            to: 'https://chimneyconfig.premodul.se/Premodul/PCMConfigurator/Index/External_Sweden',
        },

    ]

    // Filter nav items based on role, unauthenticated and newConstruction flags
    const filteredNav = nav
        .filter(item => {
            // Hide Finance menu for non-admin users
            if (!isAdmin && item.icon === 'Economy') {
                return false;
            }
            if (unauthenticated) {
                return item.unauthenticated;
            }
            return true;
        })
        .map(item => {
            if (item.children) {
                const filteredChildren = item.children.filter(child => {
                    if (unauthenticated) {
                        return child.unauthenticated;
                    }
                    return true;
                });
                return { ...item, children: filteredChildren };
            }
            return item;
        })
        .filter(item => !item.children || item.children.length > 0); // Remove empty parent items


    function currentPage() {
        let pathname = window.location.pathname,
            elem = document.querySelector('.menu a[href="' + pathname + '"]');

        if (elem) {
            currentItem(elem);
        }
    }

    function currentItem(o) {
        let self = o.target || o,
            openItems = self.closest('.menu').querySelectorAll('.menu-item-current'),
            menuItem = self.parentElement,
            menuItemHasChildren = menuItem.classList.contains('menu-item--has-children'),
            menuItemLevelOne = menuItem.classList.contains('menu-item--level-1'),
            menuItemLevelTwo = menuItem.classList.contains('menu-item--level-2'),
            selfHasOpenItems = menuItem.classList.contains('menu-item-current');

        if (!selfHasOpenItems || menuItemLevelTwo) {
            for (var i = 0; i < openItems.length; i++) {
                openItems[i].classList.remove('menu-item-current');
            }
        }

        if (menuItemHasChildren) {
            menuItem.classList.toggle('menu-item-current');
        } else if (menuItemLevelOne) {
            menuItem.classList.add('menu-item-current');
        } else if (menuItemLevelTwo) {
            let parentItem = self.closest('.menu-item--level-1');
            parentItem.classList.add('menu-item-current');
            menuItem.classList.add('menu-item-current');
        } else {
            menuItem.classList.add('menu-item-current');
        }
    }
    const normalizePath = (baseName, path) => {
        const fullBase = baseName.startsWith('/') ? baseName : '/' + baseName;
        const cleanPath = path.startsWith('/') ? path : '/' + path;

        return cleanPath.startsWith(fullBase)
            ? cleanPath
            : fullBase + cleanPath;
    };

    function listItems(items) {
        return items.map((item, index) => {

            if (item.children) {
                return (
                    <div
                        key={index}
                        className="menu-item menu-item--level-1 menu-item--has-children"
                        data-id={index + 1}
                    >
                        <span onClick={currentItem} className="menu-item-link menu-item-link--level-1">
                            <Icon name={item.icon} />
                            {item.title}
                        </span>
                        <span className="menu-toggle-child" onClick={currentItem}></span>
                        <div className="menu menu--level-2">

                            {item.children.map((child, childIndex) => {
                                let childTo = child.to;
                                if (child.unauthenticated) {
                                    childTo = normalizePath(baseName, childTo);
                                }
                                return (
                                    <div
                                        key={`${index}.${childIndex}`}
                                        className="menu-item menu-item--level-2"
                                        data-id={`${index + 1}.${childIndex + 1}`}
                                    >
                                        <Link
                                            to={childTo}
                                            onClick={currentItem}
                                            className="menu-item-link menu-item-link--level-2"
                                        >
                                            {child.title}
                                        </Link>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            }

            return (
                <div
                    key={item.to || item.title}
                    className="menu-item menu-item--level-1"
                    data-id={index + 1}
                >
                    <Link
                        to={item.to}
                        onClick={currentItem}
                        className="menu-item-link menu-item-link--level-1"
                    >
                        <Icon name={item.icon} />
                        {item.title}
                    </Link>
                </div>
            );
        });
    }
    function unFiltredListItems(items) {
        return items.map((item, index) =>
            item.children ? (
                <div
                    key={index}
                    className="menu-item menu-item--level-1 menu-item--has-children"
                    data-id={index + 1}
                >
                    <span onClick={currentItem} className="menu-item-link menu-item-link--level-1">
                        <Icon name={item.icon} />
                        {item.title}
                    </span>
                    <span className="menu-toggle-child" onClick={currentItem}></span>
                    <div className="menu menu--level-2">
                        {item.children.map((child, childIndex) => (
                            <div
                                key={childIndex}
                                className="menu-item menu-item--level-2"
                                data-id={index + 1 + '.' + (childIndex + 1)}
                            >
                                {child.external ? (
                                    <a
                                        href={child.to}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="menu-item-link menu-item-link--level-2"
                                    >
                                        {child.title}
                                    </a>
                                ) : (
                                    <Link
                                        to={normalizePath(baseName, child.to)}
                                        onClick={currentItem}
                                        className="menu-item-link menu-item-link--level-2"
                                    >
                                        {child.title}
                                    </Link>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <div
                    key={item.to || item.title}
                    className="menu-item menu-item--level-1"
                    data-id={index + 1}
                >
                    {item.external ? (
                        <a
                            href={item.to}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="menu-item-link menu-item-link--level-1"
                        >
                            {item.icon && <Icon name={item.icon} />}
                            {item.title}
                        </a>
                    ) : (
                        <Link
                            to={normalizePath(baseName, item.to)}
                            onClick={currentItem}
                            className="menu-item-link menu-item-link--level-1"
                        >
                            {item.icon && <Icon name={item.icon} />}
                            {item.title}

                        </Link>
                    )}
                </div>
            )
        );
    }

    // {listItems(filteredNav)}
    return <div className="menu menu--level-1">
        {newConstruction ? unFiltredListItems(newConstructionNav) : listItems(filteredNav)}

    </div>;
}